import React, { useEffect, useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
const QuickSavingsEstimator = () => {
  // State for slider values
  const [employees, setEmployees] = useState(50);
  const [drinksPerDay, setDrinksPerDay] = useState(2);
  const [currentCost, setCurrentCost] = useState(1.85);
  const [replacementRate, setReplacementRate] = useState(80);
  // State for calculated results
  const [annualSavings, setAnnualSavings] = useState({
    min: 0,
    max: 0
  });
  const [bottlesSaved, setBottlesSaved] = useState(0);
  const [co2Avoided, setCo2Avoided] = useState(0);
  // Constants
  const WORK_DAYS_PER_YEAR = 260; // 52 weeks × 5 days
  const SMARTSODA_COST_PER_DRINK = 0.3; // Updated to 30 cents per drink
  const BASE_MONTHLY_COST = 350; // $350 per month per 250 employees
  const EMPLOYEES_PER_MACHINE = 250;
  const CO2_PER_BOTTLE_KG = 0.082;
  // Calculate results when inputs change
  useEffect(() => {
    // Total annual drinks
    const totalDrinks = employees * drinksPerDay * WORK_DAYS_PER_YEAR;
    // Drinks replaced by SmartSoda
    const replacedDrinks = totalDrinks * (replacementRate / 100);
    // Current annual spend
    const currentAnnualSpend = totalDrinks * currentCost;
    // Calculate monthly subscription based on employee count
    // We need to scale up for every 250 employees (or fraction thereof)
    const machinesNeeded = Math.ceil(employees / EMPLOYEES_PER_MACHINE);
    const monthlySubscription = BASE_MONTHLY_COST * machinesNeeded;
    // SmartSoda annual cost (subscription + per-drink cost)
    const ssAnnualCost = monthlySubscription * 12 + replacedDrinks * SMARTSODA_COST_PER_DRINK;
    // Annual savings
    const savings = currentAnnualSpend - ssAnnualCost;
    // Add a range for min/max savings (±10%)
    const minSavings = Math.max(0, savings * 0.9);
    const maxSavings = savings * 1.1;
    // Bottles saved
    const bottlesSaved = replacedDrinks;
    // CO2 avoided (kg)
    const co2Avoided = bottlesSaved * CO2_PER_BOTTLE_KG;
    // Update state
    setAnnualSavings({
      min: minSavings,
      max: maxSavings
    });
    setBottlesSaved(bottlesSaved);
    setCo2Avoided(co2Avoided);
  }, [employees, drinksPerDay, currentCost, replacementRate]);
  // Format currency
  const formatCurrency = value => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  // Format large numbers
  const formatNumber = value => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };
  return <section className="py-12 sm:py-16 bg-blue-50" data-id="element-462">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-463">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden" data-id="element-464">
          <div className="p-4 sm:p-6 md:p-8" data-id="element-465">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-navy-950 mb-2 sm:mb-3" data-id="element-466">
              Quick Savings Estimator
            </h2>
            <p className="text-center text-gray-600 text-sm sm:text-base mb-6 sm:mb-8 max-w-2xl mx-auto" data-id="element-467">
              Get a ballpark estimate of your potential savings with SmartSoda.
              Adjust the parameters below to see how they affect your results.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 mb-6 sm:mb-8" data-id="element-468">
              {/* Number of Employees */}
              <div className="space-y-1" data-id="element-469">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-470">
                  <label htmlFor="employees" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-471">
                    Number of Employees
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-472">
                    {employees} employees
                  </span>
                </div>
                <input type="range" id="employees" min="10" max="500" step="10" value={employees} onChange={e => setEmployees(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-473" />
                <div className="flex justify-between mt-0.5" data-id="element-474">
                  <span className="text-xs text-gray-500" data-id="element-475">10</span>
                  <span className="text-xs text-gray-500" data-id="element-476">500</span>
                </div>
              </div>
              {/* Drinks Per Day */}
              <div className="space-y-1" data-id="element-477">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-478">
                  <label htmlFor="drinksPerDay" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-479">
                    Avg. Drinks Per Employee Per Day
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-480">
                    {drinksPerDay} drinks
                  </span>
                </div>
                <input type="range" id="drinksPerDay" min="1" max="5" step="0.5" value={drinksPerDay} onChange={e => setDrinksPerDay(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-481" />
                <div className="flex justify-between mt-0.5" data-id="element-482">
                  <span className="text-xs text-gray-500" data-id="element-483">1</span>
                  <span className="text-xs text-gray-500" data-id="element-484">5</span>
                </div>
              </div>
              {/* Current Cost Per Drink */}
              <div className="space-y-1" data-id="element-485">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-486">
                  <label htmlFor="currentCost" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-487">
                    Current Cost Per Drink ($)
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-488">
                    ${currentCost.toFixed(2)}
                  </span>
                </div>
                <input type="range" id="currentCost" min="0.5" max="3" step="0.05" value={currentCost} onChange={e => setCurrentCost(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-489" />
                <div className="flex justify-between mt-0.5" data-id="element-490">
                  <span className="text-xs text-gray-500" data-id="element-491">$0.50</span>
                  <span className="text-xs text-gray-500" data-id="element-492">$3.00</span>
                </div>
              </div>
              {/* Replacement Rate */}
              <div className="space-y-1" data-id="element-493">
                <div className="flex justify-between mb-1 sm:mb-2" data-id="element-494">
                  <label htmlFor="replacementRate" className="block text-xs sm:text-sm font-medium text-gray-700" data-id="element-495">
                    % of Drinks Replaced by SmartSoda
                  </label>
                  <span className="text-xs sm:text-sm text-gray-500" data-id="element-496">
                    {replacementRate}%
                  </span>
                </div>
                <input type="range" id="replacementRate" min="50" max="100" step="5" value={replacementRate} onChange={e => setReplacementRate(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-497" />
                <div className="flex justify-between mt-0.5" data-id="element-498">
                  <span className="text-xs text-gray-500" data-id="element-499">50%</span>
                  <span className="text-xs text-gray-500" data-id="element-500">100%</span>
                </div>
              </div>
            </div>
            {/* Results Section */}
            <div className="bg-blue-50 p-4 sm:p-6 rounded-lg sm:rounded-xl mb-6 sm:mb-8" data-id="element-501">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6" data-id="element-502">
                <div className="text-center bg-white p-4 rounded-lg shadow-sm" data-id="element-503">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1 sm:mb-2" data-id="element-504">
                    Estimated Annual Savings
                  </h3>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600" data-id="element-505">
                    {formatCurrency(annualSavings.min)}-
                    {formatCurrency(annualSavings.max)}
                  </p>
                </div>
                <div className="text-center bg-white p-4 rounded-lg shadow-sm" data-id="element-506">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1 sm:mb-2" data-id="element-507">
                    Environmental Impact
                  </h3>
                  <div className="flex flex-col gap-1" data-id="element-508">
                    <p className="text-base sm:text-lg md:text-xl font-bold text-green-600" data-id="element-509">
                      {formatNumber(bottlesSaved)} bottles eliminated
                    </p>
                    <p className="text-sm md:text-md text-green-600" data-id="element-510">
                      {formatNumber(co2Avoided)} kg CO₂ avoided
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-4" data-id="element-511">
                *Estimates are subject to proposal and may vary based on
                specific requirements, seasonality, and availability.
              </p>
            </div>
            {/* CTA Button */}
            <div className="text-center" data-id="element-512">
              <a href="#book-demo" className="inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-lg shadow-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all" data-id="element-513">
                Request Detailed Proposal
                <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" data-id="element-514" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default QuickSavingsEstimator;